import type { Hotel, LoopTourSummary } from '@/config/objects/hotels';
import {
  Button,
  ConfirmDialog,
  EmptyTableState,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Map, Plus, RotateCw, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { LoopTourSelector } from './LoopTourSelector';

type LoopVariant = 'short' | 'long';

const LOOP_VARIANT_LABELS: Record<LoopVariant, string> = {
  short: 'Short',
  long: 'Long',
};

/** Returns the assigned loop tour for a variant, if any. */
function loopTourFor(hotel: Hotel, variant: LoopVariant): LoopTourSummary | undefined {
  return variant === 'short' ? hotel.shortLoopTour : hotel.longLoopTour;
}

/** Small meta row shared by the editable slot and the read-only card. */
function LoopTourMeta({ tour }: { tour: LoopTourSummary }) {
  return (
    <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
      {tour.stops != null && <span>{tour.stops} stops</span>}
      {tour.duration != null && <span>{tour.duration} min</span>}
      {tour.difficulty && <span>{tour.difficulty}</span>}
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700">
        <RotateCw className="h-3 w-3" />
        Defined path
      </span>
    </div>
  );
}

interface LoopTourSlotProps {
  hotel: Hotel;
  variant: LoopVariant;
}

/**
 * Editable loop-tour slot (internal users): shows the assigned tour with a
 * remove control, or an assign button when empty. Owns its selector dialog and
 * remove confirmation and targets the variant-scoped, internal-only route.
 */
function LoopTourSlot({ hotel, variant }: LoopTourSlotProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showSelector, setShowSelector] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const label = LOOP_VARIANT_LABELS[variant];
  const loopTour = loopTourFor(hotel, variant);

  // Both loop tours are ineligible in either picker: a tour can't be this
  // hotel's short and long loop at once, and re-picking the variant's own tour
  // is a no-op.
  const loopTourIds = [
    hotel.shortLoopTour?.id ?? hotel.shortLoopTourId,
    hotel.longLoopTour?.id ?? hotel.longLoopTourId,
  ].filter((id): id is string => id != null);

  const setLoopTourMutation = useMutation({
    mutationFn: async (tourId: string) =>
      apiClient.put(`/hotels/${hotel.id}/loop-tour/${variant}`, { tourId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Hotel', hotel.id] });
      setShowSelector(false);
    },
  });

  const removeLoopTourMutation = useMutation({
    mutationFn: async () => apiClient.delete(`/hotels/${hotel.id}/loop-tour/${variant}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Hotel', hotel.id] });
    },
  });

  const handleSetLoopTour = (tourId: string) => {
    toast.promise(setLoopTourMutation.mutateAsync(tourId), {
      loading: `Setting ${label.toLowerCase()} loop tour...`,
      success: `${label} loop tour assigned`,
      error: (err: Error) => err?.message || 'Failed to set loop tour',
    });
  };

  const handleRemoveLoopTour = () => {
    toast.promise(removeLoopTourMutation.mutateAsync(), {
      loading: `Removing ${label.toLowerCase()} loop tour...`,
      success: `${label} loop tour removed`,
      error: (err: Error) => err?.message || 'Failed to remove loop tour',
    });
  };

  return (
    <>
      <div>
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label} Loop
        </div>
        {loopTour ? (
          <div className="relative rounded-lg border border-emerald-200 bg-emerald-50/50">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowRemoveConfirm(true)}
              className="absolute right-2 top-2 h-7 w-7 text-muted-foreground hover:bg-emerald-100 hover:text-destructive"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
            <Link
              to={`/tours/${loopTour.id}`}
              className="group block rounded-lg p-4 pr-10 transition-colors hover:bg-emerald-50"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                  <RotateCw className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-zinc-900 group-hover:text-emerald-700">
                    {loopTour.name}
                  </div>
                  <LoopTourMeta tour={loopTour} />
                </div>
              </div>
            </Link>
          </div>
        ) : (
          <EmptyTableState
            className="h-44 rounded-lg border border-dashed py-0"
            icon={<RotateCw className="h-6 w-6" />}
            title={`No ${label.toLowerCase()} loop tour`}
            action={{
              label: `Assign ${label} Loop Tour`,
              onClick: () => setShowSelector(true),
              variant: 'outline',
              icon: Plus,
            }}
          />
        )}
      </div>

      <LoopTourSelector
        open={showSelector}
        onOpenChange={setShowSelector}
        hotelId={hotel.id}
        greaterAreaId={hotel.greaterAreaId}
        variantLabel={label}
        excludeTourIds={loopTourIds}
        onSelect={handleSetLoopTour}
        isLoading={setLoopTourMutation.isPending}
      />

      <ConfirmDialog
        open={showRemoveConfirm}
        onOpenChange={setShowRemoveConfirm}
        title={`Remove ${label} Loop Tour`}
        description={`Remove "${loopTour?.name}" as the ${label.toLowerCase()} loop tour for this hotel? The tour itself won't be deleted.`}
        confirmText="Remove"
        variant="destructive"
        loading={removeLoopTourMutation.isPending}
        onConfirm={handleRemoveLoopTour}
      />
    </>
  );
}

interface LoopToursProps {
  hotel: Hotel;
  className?: string;
}

/**
 * Loop tours management panel (internal users only). A hotel can have an
 * optional Short and Long loop tour; renders one editable slot per variant.
 */
export function LoopTourManager({ hotel, className }: LoopToursProps) {
  return (
    <div className={`flex flex-col rounded-xl border bg-card shadow-sm ${className ?? ''}`}>
      <div className="border-b px-6 py-4">
        <h2 className="text-lg font-medium tracking-tight text-zinc-900">Loop Tours</h2>
        <p className="text-sm text-muted-foreground">
          The featured circular routes for this hotel. The short version is shown
          to guests first. Loop tours are curated by Destination Tours.
        </p>
      </div>
      <div className="flex-1 space-y-5 p-6">
        <LoopTourSlot hotel={hotel} variant="short" />
        <LoopTourSlot hotel={hotel} variant="long" />
      </div>
    </div>
  );
}

interface ReadOnlyLoopCardProps {
  tour: LoopTourSummary;
}

/**
 * Read-only visual card for a single assigned loop tour (hotel users).
 * Not clickable — hotel users don't have access to the tour detail page yet
 * (a hotel-safe tour preview is a planned follow-up).
 */
function ReadOnlyLoopCard({ tour }: ReadOnlyLoopCardProps) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
      <div className="relative h-32 w-full bg-muted">
        {tour.featuredImage?.url ? (
          <img
            src={tour.featuredImage.url}
            alt={tour.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Map className="h-8 w-8 text-muted-foreground/40" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="font-medium text-zinc-900">{tour.name}</div>
        <LoopTourMeta tour={tour} />
      </div>
    </div>
  );
}

/** A single read-only loop slot: a labelled header with the tour card or an empty placeholder. */
function ReadOnlyLoopSlot({ tour, variant }: { tour?: LoopTourSummary; variant: LoopVariant }) {
  const label = LOOP_VARIANT_LABELS[variant];
  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label} Loop
      </div>
      {tour ? (
        <ReadOnlyLoopCard tour={tour} />
      ) : (
        <EmptyTableState
          className="h-44 rounded-lg border border-dashed py-0"
          icon={<RotateCw className="h-6 w-6" />}
          title={`No ${label.toLowerCase()} loop tour yet`}
        />
      )}
    </div>
  );
}

/**
 * Read-only loop tours view (hotel users). Always shows a Short and a Long
 * slot — each with its own header and either the assigned tour card or an
 * empty placeholder — so the section keeps a consistent footprint. Hotels
 * can't assign or remove loop tours; that's curated internally.
 */
export function LoopToursReadOnly({ hotel, className }: LoopToursProps) {
  return (
    <div className={`flex flex-col rounded-xl border bg-card shadow-sm ${className ?? ''}`}>
      <div className="border-b px-6 py-4">
        <h2 className="text-lg font-medium tracking-tight text-zinc-900">Loop Tours</h2>
        <p className="text-sm text-muted-foreground">
          Featured circular routes curated for your hotel.
        </p>
      </div>
      <div className="flex-1 space-y-5 p-6">
        <ReadOnlyLoopSlot tour={hotel.shortLoopTour} variant="short" />
        <ReadOnlyLoopSlot tour={hotel.longLoopTour} variant="long" />
      </div>
    </div>
  );
}
